import React from 'react'
import { Link } from '@inertiajs/react'
import AppLayout from '@/layouts/AppLayout'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Plus } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { ja } from 'date-fns/locale'

type Repository = {
  id: number
  name: string
  full_name: string
  visibility: 'public_visibility' | 'private_visibility'
  created_at: string
}

type Pagination = {
  page: number
  pages: number
  prev: number | null
  next: number | null
}

type Props = {
  repositories: Repository[]
  pagination: Pagination
}

export default function RepositoriesIndex({ repositories, pagination }: Props) {
  return (
    <AppLayout>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">リポジトリ</h1>
        <Link href="/repositories/new">
          <Button>
            <Plus className="w-4 h-4 mr-2" />
            新規作成
          </Button>
        </Link>
      </div>

      {repositories.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <p>リポジトリがありません</p>
          <Link href="/repositories/new" className="text-primary hover:underline">
            最初のリポジトリを作成しましょう
          </Link>
        </div>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>名前</TableHead>
                <TableHead>公開設定</TableHead>
                <TableHead>作成日</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {repositories.map((repo) => (
                <TableRow key={repo.id}>
                  <TableCell>
                    <Link
                      href={`/repositories/${repo.id}`}
                      className="font-medium hover:underline text-foreground"
                    >
                      {repo.full_name}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={repo.visibility === 'public_visibility' ? 'default' : 'secondary'}
                    >
                      {repo.visibility === 'public_visibility' ? 'Public' : 'Private'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDistanceToNow(new Date(repo.created_at), {
                      addSuffix: true,
                      locale: ja,
                    })}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {pagination.pages > 1 && (
            <div className="flex justify-center gap-2 mt-6">
              {pagination.prev && (
                <Link href={`/repositories?page=${pagination.prev}`}>
                  <Button variant="outline">前へ</Button>
                </Link>
              )}
              <span className="flex items-center px-4 text-sm text-muted-foreground">
                {pagination.page} / {pagination.pages}
              </span>
              {pagination.next && (
                <Link href={`/repositories?page=${pagination.next}`}>
                  <Button variant="outline">次へ</Button>
                </Link>
              )}
            </div>
          )}
        </>
      )}
    </AppLayout>
  )
}
